import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/brand";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0B1324]/80 border-b border-white/[0.07]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-edge flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-4 group"
        >
          <span
            aria-hidden
            className="font-display text-[20px] leading-none tracking-[-0.04em] font-extrabold text-white group-hover:text-brand-400 transition-colors"
          >
            AC
          </span>
          <span
            aria-hidden
            className="hidden sm:block w-px h-7 bg-white/15"
          />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-[15px] tracking-tight text-white">
              {BRAND.name}
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/55">
              {BRAND.tagline}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace(/\W+/g, "") || "home"}`}
              className={({ isActive }) =>
                `px-4 py-2 text-[13.5px] tracking-tight transition-colors ${
                  isActive
                    ? "text-white"
                    : "text-white/60 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Link
            to="/kontakt"
            data-testid="nav-cta-kontakt"
            className="btn-primary text-[13.5px] py-2.5 px-5"
          >
            Termin anfragen <ArrowUpRight size={16} strokeWidth={1.75} />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-white/10 text-white/80"
          aria-label="Menü"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden backdrop-blur-xl bg-[#0B1324]/95 border-t border-white/[0.07]"
          >
            <div className="container-edge py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-mobile-link-${l.to.replace(/\W+/g, "") || "home"}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base tracking-tight border-b border-white/5 ${
                      isActive ? "text-white" : "text-white/70"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/kontakt"
                data-testid="nav-mobile-cta"
                className="btn-primary mt-4 justify-center"
              >
                Termin anfragen <ArrowUpRight size={16} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
